import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import SearchBox from "@/components/feature/SearchBox";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影分类", to: "/category" },
  { label: "影视资讯", to: "/news" },
  { label: "演员资料", to: "/actors" },
  { label: "关于我们", to: "/about" },
];

interface SiteHeaderProps {
  overlay?: boolean;
}

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isTransparent = overlay && !scrolled && !menuOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        isTransparent
          ? "bg-gradient-to-b from-foreground-950/70 via-foreground-950/25 to-transparent"
          : "border-b border-background-200/80 bg-background-50/95 backdrop-blur"
      }`}
    >
      <div className="flex h-[68px] w-full items-center gap-4 px-4 md:h-[76px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md md:h-10 md:w-10 ${
              isTransparent ? "bg-background-50/15 text-background-50" : "bg-primary-500 text-background-50"
            }`}
          >
            <i className="ri-film-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-[17px] font-semibold tracking-wide md:text-lg ${
                isTransparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              蜜桃成熟时
            </span>
            <span
              className={`mt-1 text-[9px] uppercase tracking-[0.24em] md:text-[10px] ${
                isTransparent ? "text-background-100/70" : "text-foreground-400"
              }`}
            >
              Peach Cinema Archive
            </span>
          </span>
        </Link>

        <nav className="mx-auto hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-[13px] transition-colors ${
                  isActive
                    ? isTransparent
                      ? "bg-background-50/15 font-medium text-background-50"
                      : "bg-primary-100 font-medium text-accent-700"
                    : isTransparent
                      ? "text-background-100/85 hover:text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <SearchBox
            variant={isTransparent ? "dark" : "light"}
            className="hidden w-[240px] md:block xl:w-[280px]"
          />
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            className={`flex h-10 w-10 items-center justify-center rounded-md border transition-colors lg:hidden ${
              isTransparent
                ? "border-background-50/30 text-background-50"
                : "border-background-300 text-foreground-800 hover:border-primary-400"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="h-[calc(100dvh-68px)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-8 pt-5 lg:hidden">
          <SearchBox className="w-full" placeholder="搜索电影、演员或关键词" />
          <nav className="mt-5 flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center justify-between border-b border-background-200/80 py-3.5 text-sm ${
                    isActive ? "font-medium text-accent-600" : "text-foreground-700"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-300" />
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 rounded-lg bg-background-100 p-4">
            <p className="font-heading text-sm font-semibold text-foreground-900">快速浏览</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/category?tag=经典系列"
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700"
              >
                经典系列
              </Link>
              <Link
                to="/category?tag=最新上映"
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700"
              >
                最新上映
              </Link>
              <Link
                to="/news"
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700"
              >
                影视资讯
              </Link>
              <Link
                to="/actors"
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700"
              >
                演员资料
              </Link>
            </div>
          </div>
          <p className="mt-6 text-[11px] leading-relaxed text-foreground-400">
            本站为影视资料演示站点，内容均为模拟数据，不提供在线播放与下载服务。
          </p>
        </div>
      )}
    </header>
  );
}